import { JSX, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

import AlertBanner from "lib/components/alert/AlertBanner";
import AlertMessage from "lib/components/alert/AlertMessage";
import ListInfoProvider from "lib/list/ListInfoProvider";

import {
	mergeState,
	ResponseState,
	ResponseStateType
} from "utils/fetch/FetchResponse";
import useFetch from "utils/fetch/useFetch";
import useGet from "utils/fetch/useGet";
import { asTitle } from "utils/Utils";

import TrajectoryClient from "trajectory/TrajectoryClient";
import { TrajectoryInfo, TrajectoryRight } from "trajectory/TrajectoryService";

import BlueprintClient from "core/blueprint/base/BlueprintClient";
import { BlueprintInfo, Right } from "core/blueprint/base/BlueprintService";
import { getBlueprintTypeString } from "core/blueprint/base/Utils";
import { Status } from "document/StatusTypes";

import TrashActionbar from "./TrashActionbar";
import TrashTable from "./TrashTable";
import { TrashItem } from "./TrashTypes";

const TRAJECTORY_PREFIX = "trajectory";
const BLUEPRINT_PREFIX = "blueprint";

export default function TrashPage(): JSX.Element {
	const { t } = useTranslation();

	const { call } = useFetch();

	const trajectoryCall = useGet<Array<TrajectoryInfo>>(
		TrajectoryClient.getAllWithStatus(Status.DELETED)
	);

	const blueprintCall = useGet<Array<BlueprintInfo>>(
		BlueprintClient.getAllWithStatus(Status.DELETED)
	);

	const state: ResponseStateType = useMemo(
		() => mergeState(trajectoryCall.response, blueprintCall.response),
		[trajectoryCall.response, blueprintCall.response]
	);

	const list: Array<TrashItem> = useMemo(() => {
		if (state !== ResponseState.SUCCESS) {
			return [];
		}

		const data: Array<TrashItem> = [];
		if (trajectoryCall.response.state === ResponseState.SUCCESS) {
			for (const trajectory of trajectoryCall.response.data) {
				data.push({
					id: `${TRAJECTORY_PREFIX}-${trajectory.id}`,
					name: trajectory.name,
					type: t("trajectory"),
					admin: trajectory.rights.includes(TrajectoryRight.METADATA),
					statusModifiedOn: trajectory.statusModifiedOn,
					statusModifiedByEmail: trajectory.statusModifiedByEmail
				});
			}
		}

		if (blueprintCall.response.state === ResponseState.SUCCESS) {
			for (const blueprint of blueprintCall.response.data) {
				data.push({
					id: `${BLUEPRINT_PREFIX}-${blueprint.id}`,
					name: blueprint.name,
					type: `${t("blueprint")} - ${t(
						getBlueprintTypeString(blueprint.type)
					)}`,
					admin: blueprint.rights.includes(Right.ADMIN),
					statusModifiedOn: blueprint.statusModifiedOn,
					statusModifiedByEmail: blueprint.statusModifiedByEmail
				});
			}
		}

		return data;
	}, [state]);

	const [alert, setAlert] = useState<AlertMessage>();

	const getIds = useCallback(
		(ids: Array<string>, prefix: string): Array<number> => {
			return ids
				.filter(id => id.startsWith(prefix))
				.map(id => id.substring(prefix.length + 1))
				.map(Number);
		},
		[]
	);

	const clearAlert = useCallback((): void => setAlert(undefined), []);

	const handleRestore = useCallback(
		(ids: Array<string>): Promise<void> => {
			const trajectoryIds = getIds(ids, TRAJECTORY_PREFIX);
			const blueprintIds = getIds(ids, BLUEPRINT_PREFIX);

			const calls: Array<Promise<void>> = [];
			for (const id of trajectoryIds) {
				calls.push(
					call(
						TrajectoryClient.updateStatus(id, {
							status: Status.LIVE
						})
					)
				);
			}

			for (const id of blueprintIds) {
				calls.push(
					call(
						BlueprintClient.updateStatus(id, {
							status: Status.LIVE
						})
					)
				);
			}

			return Promise.all(calls)
				.catch(() =>
					setAlert(AlertMessage.error(t("restoreTrashError")))
				)
				.finally(() => {
					trajectoryCall.refresh();
					blueprintCall.refresh();
				})
				.then();
		},
		[call, getIds]
	);

	const handleDelete = useCallback(
		(ids: Array<string>): Promise<void> => {
			const trajectoryIds = getIds(ids, TRAJECTORY_PREFIX);
			const blueprintIds = getIds(ids, BLUEPRINT_PREFIX);

			const calls: Array<Promise<void>> = [];
			for (const id of trajectoryIds) {
				calls.push(call(TrajectoryClient.delete(id)));
			}

			for (const id of blueprintIds) {
				calls.push(call(BlueprintClient.delete(id)));
			}

			return Promise.all(calls)
				.catch(() =>
					setAlert(AlertMessage.error(t("deleteTrashError")))
				)
				.finally(() => {
					trajectoryCall.refresh();
					blueprintCall.refresh();
				})
				.then();
		},
		[call, getIds]
	);

	const isEnabled = useCallback(
		(id: string): boolean =>
			list.find(item => item.id === id)?.admin ?? false,
		[list]
	);

	return (
		<ListInfoProvider
			keys={list.map(item => item.id)}
			isEnabled={isEnabled}>
			<Typography variant="h1">{asTitle(t("trash"))}</Typography>

			<TrashActionbar onRestore={handleRestore} onDelete={handleDelete} />
			<Box sx={{ marginTop: 2 }} />

			{state === ResponseState.ERROR && (
				<AlertBanner
					alert={AlertMessage.error(
						t("loadDocumentError", {
							document: "trash"
						})
					)}
				/>
			)}

			{state === ResponseState.LOADING && (
				<Box display="flex" justifyContent="center">
					<CircularProgress />
				</Box>
			)}

			{state === ResponseState.SUCCESS && (
				<>
					{alert && (
						<AlertBanner alert={alert} onClose={clearAlert} />
					)}
					<TrashTable items={list} />
				</>
			)}
		</ListInfoProvider>
	);
}
